import Text from '@cartwhl/ui/base/TextNew';

export const PRICING_CREDITS_EXPLAINER_ID = 'pricing-credits-explainer';

interface CreditCostPair {
  label: string;
  value: string;
}

interface CreditCostRowData {
  title: string;
  subtitle?: string;
  value?: string;
  pairs?: CreditCostPair[];
}

const CREDIT_COST_ROWS: CreditCostRowData[] = [
  {
    title: 'Text-to-motion generation',
    value: '1 credits / sec',
  },
  {
    title: 'Motion capture',
    subtitle: '(Video-to-motion generation)',
    pairs: [
      { label: 'Motion capture', value: '1 credit / person / sec' },
      { label: 'Facial capture', value: '1 credit / sec / 2 faces' },
    ],
  },
  {
    title: 'Mascot generation',
    value: '9 credits / sec input video',
  },
  {
    title: 'Character generation',
    value: '60 credits / character',
  },
  {
    title: 'Character upload',
    value: 'Free',
  },
  {
    title: 'Cartographer Retargeting',
    value: 'Free',
  },
  {
    title: 'Motion export',
    value: 'Free',
  },
];

export default function PricingCreditsExplainerSection() {
  return (
    <section
      id={PRICING_CREDITS_EXPLAINER_ID}
      className="container mt-20 scroll-mt-24 lg:mt-60 lg:scroll-mt-32"
    >
      <div className="flex flex-col gap-14">
        <div className="flex flex-col gap-8 lg:gap-10">
          <Text
            as="h2"
            token={{ base: 'display/36/bold', lg: 'display/64/bold' }}
            className="text-pretty"
          >
            How credits work
          </Text>
          <Text
            as="p"
            token={{ base: '20/medium', lg: '36/medium' }}
            className="text-pretty"
          >
            Credits are used to measure usage across Cartwheel
          </Text>
          <Text
            as="div"
            token={{ base: '16/medium', lg: '24/medium' }}
            leading="body"
            className="cw-text-box-none text-tertiary max-w-[1280px]"
          >
            <ul className="marker:text-tertiary list-disc space-y-1 pl-6 lg:pl-9">
              <li>
                For most motion workflows,{' '}
                <span className="text-primary underline decoration-green-50 decoration-2 underline-offset-2">
                  1 credit = 1 second of animation
                </span>
                .
              </li>
              <li>
                Some workflows — like multi-character capture or mascot
                generation — use more credits based on complexity.
              </li>
              <li>
                <span className="text-primary underline decoration-green-50 decoration-2 underline-offset-2">
                  Every plan includes a fixed number of credits per month
                </span>{' '}
                which restart each month. You can always purchase more as needed
                in your Plan &amp; Billing tab. A la carte purchases will
                rollover to the next month.
              </li>
              <li>
                <span className="text-primary underline decoration-green-50 decoration-2 underline-offset-2">
                  Additional credits can be bought in units of 500 at $.05 per
                  credit.
                </span>
              </li>
            </ul>
          </Text>
        </div>

        <div className="flex flex-col gap-6">
          <Text
            as="h3"
            token={{ base: 'display/20/bold', lg: 'display/34/bold' }}
          >
            Credit costs
          </Text>
          <div className="border-primary rounded-[24px] border p-5 lg:p-8">
            {CREDIT_COST_ROWS.map((row, index) => (
              <CreditsCostRow
                key={row.title}
                row={row}
                isLast={index === CREDIT_COST_ROWS.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

interface CreditsCostRowProps {
  row: CreditCostRowData;
  isLast: boolean;
}

function CreditsCostRow({ row, isLast }: CreditsCostRowProps) {
  return (
    <div className="border-primary flex flex-col justify-between gap-6 border-b py-7 first:pt-3 last:border-b-0 last:pb-3 md:flex-row">
      <div className="flex flex-1 flex-col gap-2">
        <Text
          as="div"
          token={{ base: '16/medium', md: '24/medium' }}
          className="text-primary"
        >
          {row.title}
        </Text>
        {row.subtitle && (
          <Text
            as="div"
            token="14/medium"
            className="text-primary md:text-secondary"
          >
            {row.subtitle}
          </Text>
        )}
      </div>

      {row.pairs ? (
        <div className="flex flex-col gap-2 md:gap-3">
          {row.pairs.map((pair) => (
            <div className="flex justify-between gap-5 md:justify-end">
              <Text
                as="div"
                token={{ base: '14/medium', md: '20/medium' }}
                className="text-tertiary md:text-secondary whitespace-nowrap md:w-[140px]"
              >
                {pair.label}
              </Text>
              <Text
                as="div"
                token={{ base: '14/medium', md: '20/medium' }}
                className="text-tertiary md:text-secondary flex-none text-right md:w-[200px]"
              >
                {pair.value}
              </Text>
            </div>
          ))}
        </div>
      ) : (
        <Text
          as="div"
          token={{ base: '14/medium', md: '20/medium' }}
          className="text-tertiary md:text-secondary h-full md:flex-1 md:self-center md:text-right"
        >
          {row.value}
        </Text>
      )}
    </div>
  );
}
